/**
 * HTTP client for live console APIs.
 *
 * - Hierarchy: Vite `/api/hierarchy` proxy (or `VITE_API_BASE_URL`)
 * - Analytics: Vite `/api/analytics` proxy (or `VITE_ANALYTICS_BASE_URL`)
 *
 * Auth = Keycloak OIDC only (OpenAPI `bearerAuth`):
 * - `Authorization: Bearer` from the signed-in session
 * - Acting node = JWT `agentId` claim
 *
 * Demo session → hierarchy and analytics stay on fixtures.
 * Live Keycloak → hierarchy + analytics APIs only (never silent fixture swap).
 */

import { getAccessToken, isDemoSession, decodeClaims } from '@/auth/oidc'

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, message: string, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export function getApiBaseUrl(): string | null {
  const raw = import.meta.env.VITE_API_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  if (import.meta.env.DEV) return '/api/hierarchy'
  return null
}

export function getAnalyticsBaseUrl(): string | null {
  const raw = import.meta.env.VITE_ANALYTICS_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  if (import.meta.env.DEV) return '/api/analytics'
  return null
}

/** Local-only agent uuid from `.env.local` — never used in production builds. */
function getDevCallerId(): string | null {
  if (!import.meta.env.DEV) return null
  const raw = import.meta.env.VITE_CALLER_ID
  if (typeof raw !== 'string') return null
  const id = raw.trim()
  return id.length > 0 ? id : null
}

/**
 * Acting hierarchy / analytics node for a Live (Keycloak) session.
 * Prefer JWT `agentId`; in DEV fall back to `VITE_CALLER_ID` when the token
 * lacks the claim. Demo sessions never get a caller id — they stay on fixtures.
 */
export function getCallerAgentId(): string | null {
  if (isDemoSession()) return null
  const claims = decodeClaims(getAccessToken())
  const id = claims?.agentId?.trim()
  if (id && id.length > 0) return id
  return getDevCallerId()
}

function getBearerToken(): string | null {
  if (isDemoSession()) return null
  const session = getAccessToken()
  return session?.trim() ? session.trim() : null
}

/** Live hierarchy when signed in with Keycloak (Bearer + agentId) and a base URL. Demo → fixtures. */
export function hasLiveApi(): boolean {
  if (isDemoSession()) return false
  return Boolean(getApiBaseUrl() && getBearerToken() && getCallerAgentId())
}

/**
 * Live analytics for Keycloak sessions only. Demo login always uses fixture
 * report data — never the analytics mock / service.
 */
export function hasLiveAnalytics(): boolean {
  if (isDemoSession()) return false
  return Boolean(getAnalyticsBaseUrl() && getBearerToken() && getCallerAgentId())
}

function authHeaders(headers: Headers): void {
  const bearer = getBearerToken()
  if (bearer) headers.set('Authorization', `Bearer ${bearer}`)
  // analytics-service interim identity (OpenAPI callerId) — same uuid as path {id}.
  const caller = getCallerAgentId()
  if (caller) headers.set('X-Caller-Id', caller)
}

async function fetchJson<T>(
  base: string,
  path: string,
  init: RequestInit,
  unreachableMsg: string,
): Promise<T> {
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`
  const headers = new Headers(init.headers)
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  authHeaders(headers)

  let response: Response
  try {
    response = await fetch(url, { ...init, headers })
  } catch {
    throw new ApiError(0, unreachableMsg, null)
  }
  const text = await response.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }

  if (!response.ok) {
    const message =
      typeof body === 'object' &&
      body &&
      'message' in body &&
      typeof (body as { message: unknown }).message === 'string'
        ? (body as { message: string }).message
        : `Request failed (${response.status})`
    throw new ApiError(response.status, message, body)
  }

  return body as T
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = getApiBaseUrl()
  if (!base) throw new Error('VITE_API_BASE_URL is not set')
  return fetchJson(
    base,
    path,
    init,
    'Hierarchy API unreachable — start hierarchy on :3910 or port-forward the cluster service',
  )
}

export async function analyticsFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = getAnalyticsBaseUrl()
  if (!base) throw new Error('VITE_ANALYTICS_BASE_URL is not set')
  return fetchJson(
    base,
    path,
    init,
    'Analytics API unreachable — start analytics-service or set VITE_ANALYTICS_BASE_URL',
  )
}
